import { faCheck, faCopy } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import './transcribe-import.css';

/*
 * The way into LabSmarts for a report the free read cannot handle: a fax, a
 * scan, a photograph of a printout, or a PDF from a lab LabSmarts has no
 * parser for.
 *
 * Every parser in the uploadPdf Lambda reads a PDF's text layer. A picture has
 * no text layer at all; an unsupported lab's PDF has one no parser knows how
 * to place. Either way the free read returns nothing and the practitioner used
 * to be told to type the report in by hand. This screen hands them a prompt
 * instead. They give it to an AI assistant of their own along with the report,
 * it returns a CSV of what the page PRINTS, and that CSV uploads here like any
 * lab's PDF — read by parsers/csvImport.js, mapped through the same alias
 * table every lab parser uses.
 *
 * Why the prompt lives in the app rather than on the website: it is a contract
 * with parsers/csvImport.js, not marketing copy. The column names, the `@`
 * metadata rows and the `??` rule are all things that file reads, and a prompt
 * that drifts out of step with it produces a CSV that imports wrong rather than
 * one that fails. Shipping them together is what keeps them honest. The fuller
 * how-to, which can change whenever, stays on labsmarts.com.
 */

/**
 * The prompt, as the practitioner pastes it.
 *
 * Deliberately free of markdown fences and backticks. It is pasted into a chat
 * box, not rendered as a document, and a stray fence is one more thing for the
 * model to mistake for part of the format it is being asked to produce.
 *
 * Asks for a DOWNLOADABLE file first and a code block only as a fallback. A
 * code block is what an assistant gives by default, and it leaves the
 * practitioner to paste it into a text editor and save it under a name ending
 * .csv — which the Lambda routes on, and which Notepad quietly turns into
 * .csv.txt. A file the assistant wrote lands in Downloads already named right.
 * The fallback stays because not every assistant can produce a file, and a
 * prompt that only asks for one would strand those practitioners.
 *
 * "write out every row in full" guards the cost of that: an assistant building
 * a file often writes the rows into a script first, and a model writing a long
 * literal is the one that abbreviates.
 *
 * Exported so a test can assert the things csvImport.js actually depends on are
 * still in here. The two files have to agree and nothing else enforces it.
 */
export const TRANSCRIPTION_PROMPT = `You are transcribing a laboratory report so it can be imported into software. Accuracy matters far more than completeness. If this is a scan, fax, screenshot, or photo of a printed report, some characters will be hard to read. If more than one file is attached, they are pages of ONE report: transcribe them all into a single CSV, in the order they were given.

Save the result as a file I can download, named lab-report.csv. If you cannot create files, put the CSV in a code block instead. Either way, write out every row in full and add no commentary before or after it.

FORMAT

The first line is exactly this header:

    Test Name,Result,Units,Reference Range,Page

Then, if the report shows them, these rows:

    @collected,<date the specimen was collected, MM/DD/YYYY>,,,
    @lab,<name of the laboratory>,,,
    @fasting,<Yes or No>,,,

Then one row for every test result printed on the report.

NEVER INCLUDE

Do not put the patient's name, date of birth, age, sex, address, phone number, email, medical record or chart number, insurance details, specimen or accession number, or the ordering provider's name anywhere in your answer, including in the name of the file. None of it is needed: the practitioner chooses the client in the software, and the software holds everything else. Transcribe the test results and nothing else.

RULES

1. Copy the test name EXACTLY as printed, including capitalization, punctuation, parentheses, commas, and accents. Do not expand abbreviations, do not translate, do not tidy. "T4,Free(Direct)" stays "T4,Free(Direct)". "AST (SGOT)" stays "AST (SGOT)". If the name is in another language, leave it in that language.

2. Copy the result EXACTLY as printed. Keep < and > signs, keep trailing zeros, keep decimal points. "<1" stays "<1". "2.880" stays "2.880". Never round, never convert units, never recalculate anything.

3. Copy the units and the reference range as printed. If either is blank or says something like "Not Estab.", leave that cell empty.

4. Put the page number the row appeared on in the Page column. If the report arrived as several files or images, number them in the order they were attached, starting at 1.

5. If any character is unclear, put ?? in place of that character and keep the rest. A result you cannot read at all is ??. DO NOT GUESS, and do not leave the row out. A flagged row is useful; an invented number is not.

6. Include every result row, even ones that look redundant, such as both a percentage and an absolute count for the same cell type, or a value that appears again on a later page. The software sorts out duplicates.

7. SKIP rows with no result yet: Will Follow, Pending, TNP, See Note, or a blank result cell.

8. SKIP everything that is not a test result: interpretive paragraphs, methodology notes, disclaimers, performing lab addresses, patient demographics, and page headers and footers.

9. If a value is a word rather than a number, such as Negative, Trace, or Not Detected, keep the word as printed.

10. Keep the rows in the order they appear on the report.

11. Quote any cell that contains a comma, using standard CSV double quotes.

BEFORE YOU ANSWER

Re-read each number against the report once. Digits that are commonly confused on a fax or a low-resolution image are 3/8, 5/6, 0/8, 1/7, and 9/4. If a second look leaves you unsure, that character is ??.`;

/**
 * How long the button says "Copied" before going back.
 *
 * Long enough to be seen after the eye has moved to the next step, short enough
 * that a practitioner who copies, pastes, and comes back for a second go is not
 * looking at a button that still claims to have just done it.
 */
const COPIED_MS = 2000;

interface Props {
  show: boolean;
  onHide: () => void;
}

export const TranscribeImport = ({ show, onHide }: Props) => {
  const [copied, setCopied] = useState(false);
  const promptRef = useRef<HTMLPreElement>(null);
  const timer = useRef<any>(null);

  // Cleared on close as well as on unmount: reopened, the button has to offer
  // the copy again rather than claim it already happened.
  useEffect(() => {
    if (!show) setCopied(false);
    return () => clearTimeout(timer.current);
  }, [show]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(TRANSCRIPTION_PROMPT);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      /*
       * The clipboard API is refused outside a secure context and by some
       * managed-browser policies. Rather than a dead button, select the prompt
       * so the practitioner's own copy shortcut works — which is what they
       * would reach for next anyway.
       */
      const node = promptRef.current;
      if (!node) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        {/*
         * The umbrella rather than the list, which is the one place the title
         * and the link that opens it deliberately disagree.
         *
         * The link has to be short enough to sit beside Browse, so it names
         * shapes: "Photo, scan, or unsupported lab?". A title built the same way
         * is already four nouns long and would need a fifth the next time this
         * door widens. "Cannot read yet" holds every case there is, including
         * the ones not hit yet, and "yet" keeps it from reading as a permanent
         * limit on a product that adds parsers most months. The shapes are
         * named a line below, which is where recognition actually happens.
         */}
        <Modal.Title>Import a report LabSmarts cannot read yet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/*
         * Both reasons, because the practitioner cannot tell them apart from
         * the outside and the fix is identical for both. A picture has no text
         * in it; an unsupported lab prints text LabSmarts has nowhere to put.
         * Said without naming a parser or a text layer: the sentence has to
         * land for a reader who only knows the upload did not work.
         */}
        <p className="transcribe-intro">
          A photo, scan, screenshot, or fax of a report is a picture, so
          LabSmarts cannot read the values out of it. A report from a lab
          LabSmarts does not support yet prints values it cannot place. Either
          way, an AI assistant can transcribe the report first, and the file it
          gives you uploads here like any other report.
        </p>
        <ol className="transcribe-steps">
          <li>Copy the prompt below.</li>
          {/*
           * No assistants named here, though four were until 2026-10-13.
           *
           * Naming them undercut the condition in the same sentence: a reader
           * takes a named product as one LabSmarts has already cleared, and the
           * practitioner who needs the examples is the one most likely to read
           * them that way. The ones worth naming would also have been the free
           * consumer tiers, which are the least likely to be covered by any
           * agreement the practice holds.
           */}
          <li>
            Paste it into an{' '}
            <strong>
              AI assistant your practice is permitted to use for client health
              information
            </strong>
            , and attach the report. If it came as several photos, attach all of
            them to the same chat.
          </li>
          <li>
            Download the <code>.csv</code> file it gives you, then upload that
            file here. If it shows the CSV as text instead, paste it into a
            plain text file and save it with a <code>.csv</code> ending.
          </li>
        </ol>
        {/*
         * Above the prompt, not below it, because it is about step 2 and a
         * practitioner who has copied the prompt has already left.
         *
         * The asymmetry is the whole point and is worth stating plainly: the
         * prompt asks for no identifiers and the parser drops them if they come
         * back anyway, but the REPORT being attached is the lab's own page,
         * with the name and date of birth printed on it, and nothing LabSmarts
         * writes changes that.
         */}
        <p className="transcribe-note">
          The prompt asks the assistant for the test results only, so the file
          it sends back carries no name or date of birth. The report being
          attached is the lab&apos;s own page, which does. Use an account your
          practice is permitted to use for client health information, and follow
          your own privacy obligations.
        </p>
        <pre className="transcribe-prompt" ref={promptRef}>
          {TRANSCRIPTION_PROMPT}
        </pre>
        {/*
         * Said here rather than left as a surprise on the form. The ?? rule is
         * the one part of the prompt a practitioner has to understand, because
         * it is the one that asks something of them afterwards.
         */}
        <p className="transcribe-note">
          Anything the assistant could not read comes back marked{' '}
          <code>??</code>. Those values are left off the form and listed by name
          under the upload, so you can read them off the report and type them
          in. Nothing is guessed.
        </p>
      </Modal.Body>
      {/*
       * No "more about uploading" link here, though the icon beside the form
       * label opens exactly such a page.
       *
       * It would be the same page, a few inches from a control that already
       * goes there, and it is the wrong page besides: labsmarts.com/
       * upload-lab-results-help is about which labs are supported and tells
       * the reader in as many words that "PDFs must be originals, not scans,
       * photos, or faxed copies". That sentence was true when it was written.
       * Sending a practitioner who is holding a fax to read it is sending them
       * somewhere to be told their file will not work, at the exact moment
       * this screen exists to tell them it will.
       *
       * If that page ever grows a section on transcribing one, link to the
       * section rather than the page.
       */}
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={copy}>
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="mr-2" />
          {copied ? 'Copied' : 'Copy prompt'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
